"use strict";
import * as THREE from 'three';
import { Model3d } from './lib/Model3d';
import { Camera } from './lib/Camera';
import { Scene } from './lib/Scene';
import { CSG } from 'three-csg-ts';
import { math } from './lib/Drag';
import { Env } from './lib/Env';
import { ID } from './lib/ID';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';


export class Corps_room {
    static units = 10; //1 - milimetry, 10 - centymetry, 100 - metry
    static scalar = 1;
    static floorThickness: number;//[mm]
    static wallWidth: number; //[mm]
    static wallHeight: number; //[mm]
    static shapePointsIndex: number;
    shapePoints: any[];

    constructor() {
        Corps_room.floorThickness = Env.walls_dimention.floor_thickness * Corps_room.units; //[mm]
        Corps_room.wallWidth = Env.walls_dimention.wall_thickness * Corps_room.units; //[mm]
        Corps_room.wallHeight = Env.walls_dimention.wall_height * Corps_room.units; //[mm]
        Corps_room.shapePointsIndex = Env.room_layout.length ? 0 : 2;
        this.constPoints();
        this.valueRecalculation();
        this.room();
        this.windows();
        this.centering();
        Corps_room.opacity.enabled = true;
    }

    constPoints() {
        this.shapePoints = [];
        const points: THREE.Vector2[] = [];
        for (const point of Env.room_layout) {
            points.push(new THREE.Vector2(point[0] * Env.size_units * Corps_room.units, point[1] * Env.size_units * Corps_room.units));
        }
        this.shapePoints.push(points);
    }

    valueRecalculation() {
        Corps_room.wallWidth *= Env.size_units;
        Corps_room.wallHeight *= Env.size_units;
        Corps_room.floorThickness *= Env.size_units;
    }

    async room() {
        const group = new THREE.Group();
        group.name = 'room';
        Scene.scene.add(group);
        ID.room.id = group.id;
        let floor = this.floor();
        floor.position.y -= Corps_room.floorThickness;
        floor.rotation.x = - Math.PI / 2;
        group.add(floor);
        group.userData.walls = { constructPoints: [] };
        const walls = this.walls();
        walls.rotation.x = - Math.PI / 2;
        group.add(walls);
        ID.room.walls.id = walls.id;
        ID.room.floor.id = floor.id;
    }

    centering() {
        const room = new THREE.Box3().setFromObject(Scene.scene.getObjectById(ID.room.id) as THREE.Object3D);
        const size = room.getSize(new THREE.Vector3);
        Scene.scene.add(new THREE.AxesHelper());
        ( Scene.scene.getObjectById(ID.room.id) as THREE.Object3D ).position.x = -size.x / 2;
        ( Scene.scene.getObjectById(ID.room.id) as THREE.Object3D ).position.z = size.z / 2;

        if (ID.windows.id > 0) {
            ( Scene.scene.getObjectById(ID.windows.id) as THREE.Object3D ).position.x = -size.x / 2;
            ( Scene.scene.getObjectById(ID.windows.id) as THREE.Object3D ).position.z = size.z / 2;
        }
    }

    //rysuj ściany na podstawie punktów this.shapePoints[this.shapePointsIndex]
    walls() {
        const floorBasePoints = this.shapePoints[Corps_room.shapePointsIndex];
        const walls = new THREE.Group();
        walls.name = 'walls';
        let points: any[] = [];
        for (let i in floorBasePoints) {
            const iUp = parseInt(i) <= floorBasePoints.length - 2 ? parseInt(i) + 1 : 0;
            let pointA = new THREE.Vector2(floorBasePoints[i].x, floorBasePoints[i].y);
            let pointB = new THREE.Vector2(floorBasePoints[iUp].x, floorBasePoints[iUp].y);

            let auxiliaryLines = math.auxiliaryLines([pointA.x, pointA.y], [pointB.x, pointB.y], Corps_room.wallWidth);
            let line = this.selectInsideLines([pointA, pointB]);
            if (line.plus.top && line.plus.bottom && line.plus.right && line.plus.left) {
                points.push(
                    [
                        pointA,
                        pointB,
                        new THREE.Vector2(auxiliaryLines[1][1][0], auxiliaryLines[1][1][1]),
                        new THREE.Vector2(auxiliaryLines[1][0][0], auxiliaryLines[1][0][1])
                    ]
                );
            }
            if (line.minus.top && line.minus.bottom && line.minus.right && line.minus.left) {
                points.push(
                    [
                        pointA,
                        pointB,
                        new THREE.Vector2(auxiliaryLines[2][1][0], auxiliaryLines[2][1][1]),
                        new THREE.Vector2(auxiliaryLines[2][0][0], auxiliaryLines[2][0][1])
                    ]
                );
            }
        }

        for (let index in points) {
            const indexUp = parseInt(index) < points.length - 1 ? parseInt(index) + 1 : 0;
            const indexDown = parseInt(index) > 0 ? parseInt(index) - 1 : points.length - 1;
            let before = math.intersection(
                math.line([points[index][2].x, points[index][2].y], [points[index][3].x, points[index][3].y]),
                math.line([points[indexDown][2].x, points[indexDown][2].y], [points[indexDown][3].x, points[indexDown][3].y])
            );
            let after = math.intersection(
                math.line([points[index][2].x, points[index][2].y], [points[index][3].x, points[index][3].y]),
                math.line([points[indexUp][2].x, points[indexUp][2].y], [points[indexUp][3].x, points[indexUp][3].y])
            );
            points[index][3] = new THREE.Vector2(before[0], before[1]);
            points[index][2] = new THREE.Vector2(after[0], after[1]);

            const wallGroup = new THREE.Group();
            wallGroup.name = `group_wall_#${index}`;
            const geometry = new THREE.ExtrudeGeometry(new THREE.Shape(points[index]), {
                steps: 2,
                depth: Corps_room.wallHeight,
                bevelEnabled: true,
                bevelThickness: 0,
                bevelSize: 0,
                bevelOffset: 0,
                bevelSegments: 0

            });
            geometry.computeBoundingBox();
            const material: THREE.MeshBasicMaterial = Env.materialsLibrary.find(
                (material: THREE.MeshBasicMaterial) => material.userData.url === `${Env.base_url}static/corps/wall.jpg`
            ) ?? new THREE.MeshBasicMaterial({
                side: THREE.DoubleSide,
                color: 0xfafafa,
                map: this.setTexture(`${Env.base_url}static/corps/wall_plaster.jpg`, new THREE.Vector2(.5, .5), THREE.RepeatWrapping),
                // normalMap: this.setTexture(`${Env.base_url}static/corps/wall_plaster_normal.jpg`, new THREE.Vector2(.5, .5), THREE.RepeatWrapping),
                // envMap: self.envMap,
                // reflectivity: 0,
                // envMapIntensity: 5,
                userData: {
                    url: `${Env.base_url}static/corps/wall.jpg`
                }
            });
            const mesh = new THREE.Mesh(geometry, material);
            // mesh.castShadow = true;
            // mesh.receiveShadow = true;
            mesh.name = `wall_#${index}`;
            mesh.userData.index = parseInt(index);
            wallGroup.add(mesh);
            ID.room.wall.push(mesh.id);

            const sphereGeometry = new THREE.SphereGeometry(0.1 / Corps_room.scalar * Env.size_units, 64, 32);
            const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
            const sphereGroup = new THREE.Group();
            sphereGroup.name = 'sphere';
            const sphereDown = new THREE.Mesh(sphereGeometry, sphereMaterial);
            sphereDown.userData.wallName = mesh.name;
            sphereDown.position.set(points[index][3].x, points[index][3].y, 0);
            const sphereDownOut = new THREE.Mesh(sphereGeometry, sphereMaterial);
            sphereDownOut.userData.wallName = mesh.name;
            sphereDownOut.position.set(points[index][1].x, points[index][1].y, 0);
            sphereGroup.add(sphereDown, sphereDownOut); //if you need add sphereUp
            ID.room.walls.constructPoints.inside[index] = sphereDown.id;
            ID.room.walls.constructPoints.outside[index] = sphereDownOut.id;
            wallGroup.add(sphereGroup);
            walls.add(wallGroup);
        }
        return walls;
    }

    //rysuj podłogę na podstawie punktów this.shapePoints
    floor() {
        const floorGroup = new THREE.Group();
        floorGroup.name = 'floor';
        const shape = new THREE.Shape(this.shapePoints[Corps_room.shapePointsIndex]);
        const mesh = new THREE.Mesh(
            new THREE.ExtrudeGeometry(shape, {
                steps: 2,
                depth: Corps_room.floorThickness,
                bevelEnabled: true,
                bevelThickness: 0,
                bevelSize: 0,
                bevelOffset: 0,
                bevelSegments: 0
            }),
            new THREE.MeshBasicMaterial({
                side: THREE.DoubleSide,
                color: 'white',
                map: this.setTexture(`${Env.base_url}static/corps/floor_wood4.jpg`, new THREE.Vector2(.5, .5), THREE.RepeatWrapping),
                // normalMap: this.setTexture(`${Env.base_url}static/corps/floor_wood4_normal.jpg`, new THREE.Vector2(.5, .5), THREE.RepeatWrapping),
                // envMap: self.envMap,
                // envMapIntensity: self.envMapIntensity,
                // map: this.setTexture(`${Env.base_url}static/corps/wood-background.jpg`, new THREE.Vector2(.4,.4)),
                // map: this.setTexture(`${Env.base_url}static/corps/floor_wood1.jpg`, new THREE.Vector2(.4,.4)),
                // map: this.setTexture(`${Env.base_url}static/corps/floor.jpg`, new THREE.Vector2(.4,.4)),
                // map: this.setTexture(`${Env.base_url}static/corps/laminate_floor.jpg`, new THREE.Vector2(.3,.3)),
                // normalMap: this.setTexture(`${Env.base_url}static/corps/laminate_floor.png`, new THREE.Vector2(.3,.3))
                // color: 'brown',
            })
        );
        mesh.name = 'floor';
        // mesh.castShadow = true;
        // mesh.receiveShadow = true;
        floorGroup.add(mesh);
        return floorGroup;
    }

    setTexture(url: string, textureRepeat = new THREE.Vector2(1, 1), textureWrap: number = THREE.MirroredRepeatWrapping) {
        let texture: any;
        // const rotation = this.data.graining && this.data.graining === 1 ? Math.PI / 2 : 0;
        if (Env.textures.find((texture: THREE.CanvasTexture) => texture.userData.url === url)) {
            texture = Env.textures.find((texture: THREE.CanvasTexture) => texture.userData.url === url);
        } else {
            let image: any;
            try {
                if (THREE.Cache.get(url) === undefined) throw 'brak tekstury';
                image = THREE.Cache.get(url);
                texture = new THREE.CanvasTexture(image);
            } catch (e) {
                image = THREE.Cache.get(`${Env.base_url}static/corps/default.png`);
                texture = new THREE.CanvasTexture(image);
            }
            texture.repeat = textureRepeat;
            // texture.rotation = rotation;
            texture.wrapS = texture.wrapT = textureWrap;
            // texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
            texture.magFilter = THREE.NearestFilter;
            texture.minFilter = THREE.LinearFilter;
            texture.premultiplyAlpha = true;
            texture.crossOrigin = true;
            // texture.colorSpace = THREE.SRGBColorSpace;
            texture.colorSpace = THREE.LinearSRGBColorSpace;
            texture.flipY = false;
            texture.updateMatrix();
            texture.userData = {
                url: url,
                // repeat: repeat,
                // rotation: rotation
            };
            Env.textures.push(texture);
            ID.textures.push(texture.id)
        }
        texture.updateMatrix();
        return texture;
    }

    /**funkcja wybiera, które proste są w środku kształtu
     * 
     * @param {*} point 
     * @param {*} floorPts 
     * @returns 
     */
    selectInsideLines(point) {
        const floorBasePoints = this.shapePoints[Corps_room.shapePointsIndex];
        let helper = {
            plus: { top: false, bottom: false, left: false, right: false },
            minus: { top: false, bottom: false, left: false, right: false }
        };
        let auxiliaryLines = math.auxiliaryLines([point[0].x, point[0].y], [point[1].x, point[1].y], Corps_room.wallWidth);
        let iMinus = 0;
        let iPlus = 0;
        for (const i in floorBasePoints) {
            const iUp = parseInt(i) <= floorBasePoints.length - 2 ? parseInt(i) + 1 : 0;
            const intersectionMinus = math.segmentsIntersection(
                [[auxiliaryLines[2][2][0], auxiliaryLines[2][2][1]], [9867546, 1451126]],
                [[floorBasePoints[i].x, floorBasePoints[i].y], [floorBasePoints[iUp].x, floorBasePoints[iUp].y]]
            );
            const intersectionPlus = math.segmentsIntersection(
                [[auxiliaryLines[1][2][0], auxiliaryLines[1][2][1]], [9867546, 1451126]],
                [[floorBasePoints[i].x, floorBasePoints[i].y], [floorBasePoints[iUp].x, floorBasePoints[iUp].y]]
            );
            if (intersectionMinus.length) {
                iMinus++;
            }
            if (intersectionPlus.length) {
                iPlus++;
            }
        }
        if (iMinus % 2) {
            helper.minus.top = helper.minus.bottom = helper.minus.left = helper.minus.right = true;
        } else if (iPlus % 2) {
            helper.plus.top = helper.plus.bottom = helper.plus.left = helper.plus.right = true;
        } else {
        }
        return helper;
    }

    static opacity = {
        enabled: false,
        update: async function () {
            if (this.enabled && Env.cabinets_cart_data) {
                try {
                    ( Scene.scene.getObjectById(ID.room.walls.id) as THREE.Object3D ).traverse((node) => {
                        node.visible = true;
                    });
                    const cabinets = Scene.scene.getObjectById(ID.cabinets.id);
                    if (cabinets) {
                        const box3 = new THREE.Box3(new THREE.Vector3).setFromObject(cabinets);
                        const object = Scene.scene.getObjectById(ID.room.walls.id);
                        const vectors = [
                            new THREE.Vector3(box3.max.x, box3.max.y, box3.max.z),
                            new THREE.Vector3(box3.max.x, box3.max.y, box3.min.z),
                            new THREE.Vector3(box3.max.x, box3.min.y, box3.max.z),
                            new THREE.Vector3(box3.max.x, box3.min.y, box3.min.z),
                            new THREE.Vector3(box3.min.x, box3.max.y, box3.max.z),
                            new THREE.Vector3(box3.min.x, box3.max.y, box3.min.z),
                            new THREE.Vector3(box3.min.x, box3.min.y, box3.max.z),
                            new THREE.Vector3(box3.min.x, box3.min.y, box3.min.z),
                        ]
                        for (const vector of vectors) {
                            Model3d.intersectRaycaster([object], (vector).clone(), Camera.camera.position.clone().normalize()).filter((element: any) => element.object.isMesh && element.distance >= 20 * Env.size_units).forEach((node) => {
                                if (node.object.name !== 'floor')
                                    node.object.visible = false;
                            });
                        }
                    }
                } catch (e) {
                    console.debug(e);
                }
            }
        }
    }

    async windows() {
        if (Env.cabinets_cart_data) {
            if (Env.doorwindow.length) {
                // let windows: THREE.Group;
                Scene.scene.add(new THREE.AxesHelper())
                const windows = new THREE.Group();
                windows.name = 'windows';
                Scene.scene.add(windows);
                ID.windows.id = windows.id;
                windows.rotation.x = -Math.PI / 2;
                for (const data of Env.doorwindow) {
                    windows.add(await this.cutFromWall(data));
                }
            }
        }
    }

    async cutFromWall(data: any, windowRotation180 = true) {

        const wallIndex = parseInt(data.wall_index);
        const widthHole = parseFloat(data.window_width) * Corps_room.units;
        const heightHole = parseFloat(data.window_height) * Corps_room.units;
        const heightOnWall = parseFloat(data.window_positionY) * Corps_room.units;
        let alpha = parseFloat(data.window_alpha) * Corps_room.units;

        const wallIndexUp = wallIndex <= ID.room.walls.constructPoints.inside.length - 2 ? wallIndex + 1 : 0;

        const cubeMesh = new THREE.Mesh(
            new THREE.BoxGeometry(widthHole * Env.size_units, 500 * Env.size_units, heightHole * Env.size_units),
            new THREE.MeshStandardMaterial({ color: 'blue' })
        );

        const line = math.line(
            [
                ( Scene.scene.getObjectById(ID.room.walls.constructPoints.inside[wallIndex]) as THREE.Object3D ).position.x,
                ( Scene.scene.getObjectById(ID.room.walls.constructPoints.inside[wallIndex]) as THREE.Object3D ).position.y
            ],
            [
                ( Scene.scene.getObjectById(ID.room.walls.constructPoints.inside[wallIndexUp]) as THREE.Object3D ).position.x,
                ( Scene.scene.getObjectById(ID.room.walls.constructPoints.inside[wallIndexUp]) as THREE.Object3D ).position.y
            ],
        );
        const lerp = {
            p1: new THREE.Vector3(
                ( Scene.scene.getObjectById(ID.room.walls.constructPoints.inside[wallIndex]) as THREE.Object3D ).position.x,
                ( Scene.scene.getObjectById(ID.room.walls.constructPoints.inside[wallIndex]) as THREE.Object3D ).position.y,
                ( Scene.scene.getObjectById(ID.room.walls.constructPoints.inside[wallIndex]) as THREE.Object3D ).position.z + (heightHole * Env.size_units) / 2 + heightOnWall * Env.size_units
            ),
            p2: new THREE.Vector3(
                ( Scene.scene.getObjectById(ID.room.walls.constructPoints.inside[wallIndexUp]) as THREE.Object3D ).position.x,
                ( Scene.scene.getObjectById(ID.room.walls.constructPoints.inside[wallIndexUp]) as THREE.Object3D ).position.y,
                ( Scene.scene.getObjectById(ID.room.walls.constructPoints.inside[wallIndexUp]) as THREE.Object3D ).position.z + (heightHole * Env.size_units) / 2 + heightOnWall * Env.size_units
            ),
            alpha
        }
        let p1p2distance = lerp.p1.distanceTo(lerp.p2);
        alpha = alpha * Env.size_units;
        lerp.alpha = (alpha + ((widthHole / 2) * Env.size_units)) / (p1p2distance)
        cubeMesh.position.lerpVectors(lerp.p1, lerp.p2, lerp.alpha);
        cubeMesh.rotation.z = line[0] ? Math.PI / 2 : Math.atan(line[2]);
        cubeMesh.updateMatrix();

        const csgSubstract = CSG.subtract(
            ( Scene.scene.getObjectById(ID.room.wall[wallIndex]) as THREE.Mesh ),
            cubeMesh
        );

        const mesh = Scene.scene.getObjectById(ID.room.wall[wallIndex]) as THREE.Mesh;
        const parentMesh = mesh.parent as THREE.Object3D;

        parentMesh.add(
            csgSubstract
        )

        parentMesh.remove(mesh)
        ID.room.wall[wallIndex] = csgSubstract.id;

        const loader = new GLTFLoader();
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('draco/');
        loader.setDRACOLoader(dracoLoader);
        const object = (await loader.loadAsync(`${Env.base_url}corps/accessories/window_double.glb`)).scene.children[0].clone();
        const size = new THREE.Box3().setFromObject(object).getSize(new THREE.Vector3);
        object.scale.x *= (widthHole + 60) * Env.size_units / size.x;
        object.scale.y *= (heightHole + 40) * Env.size_units / size.y;
        object.scale.z *= 250 * Env.size_units / size.z;
        object.rotation.x = Math.PI / 2;
        ( Scene.scene.getObjectById(ID.room.wall[wallIndex]) as THREE.Object3D ).add(object);
        const image = THREE.Cache.get(`${Env.base_url}static/corps/sky.jpg`);
        const texture = new THREE.CanvasTexture(image);
        const viewOutside: any = object.getObjectByName('view_outside_the_window');
        if(viewOutside) {
            viewOutside.material.map = texture;
        }
        object.rotation.y = line[0] ? Math.PI / 2 : Math.atan(line[2]);
        object.position.lerpVectors(lerp.p1, lerp.p2, lerp.alpha);
        return object;
    }
}
